/**
 * Customer Service Layer
 * Handles all Firestore operations for the customers collection
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  writeBatch,
  runTransaction,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { db } from '../firebase';
import { Customer, CustomerAddress, Job, Vault, VaultItem } from '@/types';
import { reserveAccountNumber } from './account-number.service';
import { getTenantIdSync, tenantIdReadUnion } from '@/lib/tenant';
import { stripUndefinedDeep } from '@/utils/firestore';
import { normalizeAddress } from '@/utils/address';
import { dedupeAddressTags } from '@shared/domain/address-tags';

const COLLECTION_NAME = 'customers';
/** Firestore batches cap at 500 writes — leave headroom for safety */
const CASCADE_BATCH_LIMIT = 450;

function getTenantId(): string {
  return getTenantIdSync();
}

// ============================================================================
// CREATE
// ============================================================================

/**
 * Create a new customer.
 *
 * Auto-generates an account number if one isn't supplied. If the customer has a
 * parentCompanyId, the parent's companyName is denormalized onto parentCompanyName
 * so lists/cards can render the breadcrumb without an extra read.
 */
export async function createCustomer(
  data: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>,
  userId?: string
): Promise<string> {
  const tenantId = getTenantId();

  const accountNumber = data.accountNumber || (await reserveAccountNumber(tenantId));

  let parentCompanyName = data.parentCompanyName;
  if (data.parentCompanyId && !parentCompanyName) {
    const parent = await getCustomer(data.parentCompanyId);
    parentCompanyName = parent?.companyName || parent?.name || undefined;
  }

  const customerData: Omit<Customer, 'id'> = {
    ...data,
    // Always stamp a tenantId — falls back to the canonical value if the
    // caller didn't supply one (ManualJobCreationModal / DealCreationWizard
    // auto-create customers without passing tenantId). `||` catches both
    // `undefined` and empty-string, so a caller passing `tenantId: ''`
    // still gets the canonical fallback rather than an empty silo.
    tenantId: data.tenantId || tenantId,
    accountNumber,
    ...(parentCompanyName ? { parentCompanyName } : {}),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    ...(userId && { createdBy: userId }),
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), customerData);
  return docRef.id;
}

// ============================================================================
// READ
// ============================================================================

/**
 * Get a single customer by ID
 */
export async function getCustomer(customerId: string): Promise<Customer | null> {
  const docRef = doc(db, COLLECTION_NAME, customerId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as Customer;
}

/**
 * Get all customers for the current tenant.
 *
 * Filters by `tenantId` using the legacy-alias union so pre-migration
 * docs still surface. Without this filter, a multi-tenant deployment
 * would leak every tenant's customer list to every signed-in user
 * (see docs/MULTI_TENANT_AUDIT.md §4.2).
 */
export async function getAllCustomers(tenantId: string = getTenantIdSync()): Promise<Customer[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as Customer
  );
}

/**
 * Search customers by name, email, or phone within the current tenant.
 */
export async function searchCustomers(
  searchTerm: string,
  tenantId: string = getTenantIdSync()
): Promise<Customer[]> {
  const allCustomers = await getAllCustomers(tenantId);

  const lowerSearch = searchTerm.toLowerCase();

  return allCustomers.filter(
    (customer) =>
      (customer.name || '').toLowerCase().includes(lowerSearch) ||
      customer.email?.toLowerCase().includes(lowerSearch) ||
      customer.phone?.includes(searchTerm)
  );
}

/**
 * Get customers by email
 */
export async function getCustomersByEmail(email: string): Promise<Customer[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('email', '==', email),
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as Customer
  );
}

// ============================================================================
// UPDATE
// ============================================================================

/**
 * Update a customer.
 *
 * When the customer's display `name` changes, denormalised copies on
 * linked jobs/faults/deals/customer_services are cascaded so the
 * fallback queries (e.g. CustomerDetailModal's name-match for legacy
 * records) keep hitting. Cascade runs via batch writes capped at
 * Firestore's 500-per-batch limit.
 *
 * Non-name updates take the fast path — no extra reads, just the write.
 */
export async function updateCustomer(
  customerId: string,
  data: Partial<Omit<Customer, 'id' | 'createdAt' | 'createdBy'>>
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, customerId);
  const now = new Date().toISOString();

  // Detect a name change — only fetch the current doc when `name` is in the
  // update payload, since most edits touch other fields and shouldn't pay
  // for an extra round trip.
  let oldName: string | undefined;
  let newName: string | undefined;
  if (data.name !== undefined) {
    try {
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const existing = snap.data() as Customer;
        oldName = existing.name;
        newName = data.name;
      }
    } catch (err) {
      console.warn('[updateCustomer] Pre-read for name cascade failed — skipping cascade:', err);
    }
  }

  await updateDoc(docRef, {
    ...data,
    updatedAt: now,
  });

  // If the name actually changed, cascade the denormalised copies.
  // Fire-and-forget so the modal `onSaved` callback isn't blocked on a
  // long-running batch. Failures are logged but don't surface to the UI.
  if (oldName !== undefined && newName !== undefined && oldName !== newName) {
    cascadeCustomerNameChange(customerId, oldName, newName).catch((err) => {
      console.error('[updateCustomer] Name cascade failed:', err);
    });
  }

  // If the office edited the Site Vault, push the fresh credentials onto the
  // customer's OPEN jobs. Reverse of syncVaultToCustomer (job → customer,
  // PR #1257): without this, a job created before a WiFi password change
  // keeps its stale vault copy forever — and since job-level vault values
  // win in the tech-side merge, the tech would see the OLD password.
  if (data.vault !== undefined && data.vault) {
    syncCustomerVaultToOpenJobs(customerId, data.vault as Vault).catch((err) => {
      console.error('[updateCustomer] Vault → open-jobs cascade failed:', err);
    });
  }
}

/**
 * Cascade a customer Site Vault edit onto every open (non-terminal) job for
 * that customer. The customer vault is canonical at office-edit time — the
 * tech-captured path already syncs job → customer, so overwriting open jobs'
 * WiFi pair with the fresh canonical value is consistent. Job-specific
 * "other" credentials not present on the customer vault are preserved
 * (unioned) so a gate code a tech typed onto one job isn't lost.
 *
 * Terminal jobs (Completed / Cancelled / deleted) are left untouched —
 * they're a historical record of what was true at the time.
 */
export async function syncCustomerVaultToOpenJobs(
  customerId: string,
  vault: Vault,
): Promise<number> {
  const now = new Date().toISOString();
  const snap = await getDocs(query(
    collection(db, 'jobs'),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('customerId', '==', customerId),
  ));
  if (snap.empty) return 0;

  const TERMINAL = new Set(['Completed', 'Cancelled']);
  let touched = 0;

  for (const d of snap.docs) {
    const job = d.data() as {
      deleted?: boolean;
      status?: string;
      jobState?: string;
      vault?: Vault;
    };
    if (job.deleted === true) continue;
    if (TERMINAL.has(job.status as string)) continue;
    if (job.jobState === 'COMPLETED' || job.jobState === 'CANCELLED') continue;

    // Union "other" credentials: customer vault rows first (canonical),
    // then any job-only rows that aren't already covered.
    const seen = new Set(
      (vault.other || []).map((o) => `${(o.label || '').trim().toLowerCase()}|${(o.value || '').trim()}`)
    );
    const jobOnlyOther = ((job.vault?.other || []) as VaultItem[]).filter((o) => {
      const key = `${(o.label || '').trim().toLowerCase()}|${(o.value || '').trim()}`;
      if (!o.value || seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // UniFi site name: customer value is canonical when set; otherwise keep
    // whatever the tech captured on this job (the office edit surfaces don't
    // all know the field yet — never let the cascade silently wipe it).
    // Built conditionally so Firestore never sees `undefined`.
    const unifiSiteName = (vault.unifiSiteName || job.vault?.unifiSiteName || '').trim();
    const nextVault: Vault = {
      ssid: vault.ssid || '',
      pass: vault.pass || '',
      ...(unifiSiteName ? { unifiSiteName } : {}),
      other: [...(vault.other || []), ...jobOnlyOther],
    };

    // Skip no-op writes (idempotent re-saves shouldn't touch every job).
    if (JSON.stringify(job.vault || null) === JSON.stringify(nextVault)) continue;

    try {
      await updateDoc(doc(db, 'jobs', d.id), {
        vault: nextVault,
        vaultSyncedFromCustomerAt: now,
        updatedAt: now,
      });
      touched++;
    } catch (err) {
      console.warn(`[syncCustomerVaultToOpenJobs] Failed to update job ${d.id}:`, err);
    }
  }

  if (touched > 0) {
    console.log(`[syncCustomerVaultToOpenJobs] Pushed fresh vault to ${touched} open job(s) for customer ${customerId}`);
  }
  return touched;
}

/**
 * Sync a vault captured on a job (e.g. WiFi entered at job completion) up to
 * the customer's Site Vault so the office sees it automatically.
 *
 * Resolution order:
 *   1. `job.customerId` — the direct link (normal case).
 *   2. Exact name match on `job.client` within the tenant — covers orphan
 *      jobs that were never hard-linked. Mirrors how CustomerDetailModal
 *      already links jobs by name. When resolved this way the job is also
 *      back-linked (customerId stamped) so future syncs go direct.
 *
 * Ambiguity guard: if a name matches more than one customer we DON'T write —
 * putting WiFi on the wrong customer is worse than not writing, and the Site
 * Vault's "captured on a job" banner still lets the office import it manually.
 *
 * Best-effort: never throws. Returns the customerId written to, or null when
 * no unambiguous customer could be resolved.
 */
/**
 * Merge a vault captured on a JOB into the customer's canonical Site Vault
 * WITHOUT destroying office-curated credentials. WiFi (ssid/pass) and the UniFi
 * site name take the fresh job value when present (the tech was on-site);
 * `other[]` is a UNION that keeps every existing customer row (the office is
 * canonical for gate / alarm / router / portal logins) and only adds job rows
 * for labels the customer doesn't already have. Dedup is by label, so a stale
 * job copy of an office credential can't shadow, overwrite, or duplicate it.
 *
 * Exported (pure) so the daily-audit #1265 C1 regression suite can pin the
 * merge semantics — see __tests__/vault-merge.test.ts.
 */
export function mergeIncomingVaultIntoCustomer(
  existing: Vault | null | undefined,
  incoming: Vault,
): Vault {
  const existingOther = (existing?.other || []) as VaultItem[];
  const seenLabels = new Set(
    existingOther.map((o) => (o.label || '').trim().toLowerCase()).filter(Boolean),
  );
  const jobNewOther = ((incoming.other || []) as VaultItem[]).filter((o) => {
    const label = (o.label || '').trim().toLowerCase();
    if (!label || !o.value) return false; // skip blank rows
    if (seenLabels.has(label)) return false; // office already has this label — office wins
    seenLabels.add(label);
    return true;
  });
  const unifiSiteName = (incoming.unifiSiteName || existing?.unifiSiteName || '').trim();
  return {
    ssid: incoming.ssid || existing?.ssid || '',
    pass: incoming.pass || existing?.pass || '',
    ...(unifiSiteName ? { unifiSiteName } : {}),
    other: [...existingOther, ...jobNewOther],
  };
}

export async function syncVaultToCustomer(
  job: Pick<Job, 'id' | 'customerId' | 'client'>,
  vault: Vault,
): Promise<string | null> {
  const now = new Date().toISOString();
  try {
    // 1. Resolve the target customer — direct link first, then an exact
    //    tenant-scoped name match for orphan jobs.
    let customerId = job.customerId || null;
    let resolvedByName = false;

    if (!customerId) {
      const name = (job.client || '').trim();
      if (!name) return null;

      const snap = await getDocs(
        query(
          collection(db, COLLECTION_NAME),
          where('name', '==', name),
          where('tenantId', 'in', tenantIdReadUnion(getTenantId())),
        ),
      );

      if (snap.empty) {
        console.warn('[syncVaultToCustomer] no customer matched by name; vault stays on job', { name });
        return null;
      }
      if (snap.size > 1) {
        console.warn('[syncVaultToCustomer] name matched multiple customers; skipping auto-sync', {
          name,
          count: snap.size,
        });
        return null;
      }
      customerId = snap.docs[0].id;
      resolvedByName = true;
    }

    // 2. MERGE the job vault into the customer's canonical vault so
    //    office-curated credentials are never wiped. Transactional read+merge
    //    +write so a concurrent office edit can't be clobbered by a stale read.
    //    (Daily-audit #1265 C1 — the previous wholesale `updateDoc({ vault })`
    //    here silently destroyed customer.vault.other[] — gate/alarm/router/
    //    portal logins — on every job-completion vault sync.)
    await runTransaction(db, async (tx) => {
      const ref = doc(db, COLLECTION_NAME, customerId as string);
      const snap = await tx.get(ref);
      if (!snap.exists()) return; // customer deleted mid-flight — nothing to sync
      const existing = (snap.data()?.vault as Vault | undefined) ?? null;
      tx.update(ref, {
        vault: mergeIncomingVaultIntoCustomer(existing, vault),
        updatedAt: now,
      });
    });

    // Back-link the job so the next sync goes direct (best-effort).
    if (resolvedByName && job.id) {
      updateDoc(doc(db, 'jobs', job.id), { customerId }).catch((err) =>
        console.warn('[syncVaultToCustomer] job back-link failed:', err),
      );
    }
    return customerId;
  } catch (err) {
    console.warn('[syncVaultToCustomer] failed:', err);
    return null;
  }
}

/**
 * Cascade a customer name change to denormalised copies on linked records.
 *
 * Writes (only for docs that match the customerId OR the old name):
 *   - jobs.client            ← new name
 *   - jobs.linkedCustomerName ← new name
 *   - faults.clientName       ← new name
 *   - deals.customerName      ← new name
 *   - customer_services.customerName ← new name
 *
 * Matches on `customerId == this` primarily, and falls back to the old
 * name so historical name-only orphans (pre-backfill) get swept too.
 * Batched in chunks of 450 to respect Firestore's write limit.
 */
async function cascadeCustomerNameChange(
  customerId: string,
  oldName: string,
  newName: string
): Promise<void> {
  const updates: Array<{ ref: ReturnType<typeof doc>; data: Record<string, unknown> }> = [];
  const now = new Date().toISOString();

  // Helper: collect matches from a collection/field pair and push updates
  async function collect(
    collectionName: 'jobs' | 'faults' | 'deals' | 'customer_services',
    nameField: 'client' | 'clientName' | 'customerName',
    extraNameFields: string[] = [],
  ) {
    // Primary: match by customerId
    try {
      const byIdSnap = await getDocs(
        query(
          collection(db, collectionName),
          where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
          where('customerId', '==', customerId),
        )
      );
      byIdSnap.docs.forEach((d) => {
        const data = d.data();
        // Skip if the denormalised name is already correct
        if (data[nameField] === newName) return;
        const patch: Record<string, unknown> = { [nameField]: newName, updatedAt: now };
        for (const extra of extraNameFields) {
          if (data[extra] !== undefined && data[extra] !== newName) {
            patch[extra] = newName;
          }
        }
        updates.push({ ref: doc(db, collectionName, d.id), data: patch });
      });
    } catch (err) {
      console.error(`[cascadeCustomerNameChange] ${collectionName} by-id query failed:`, err);
    }

    // Fallback: match by old name to catch TRUE ORPHANS whose customerId
    // was never attached. We must NOT rewrite docs that have a different
    // customerId — those belong to a different customer who happens to
    // share the old name (e.g. two "Smith" customers). Corrupting their
    // denormalised name would break the customer-detail name-fallback
    // query the audit explicitly set out to fix.
    if (!oldName.trim()) return;
    try {
      const byNameSnap = await getDocs(
        query(
          collection(db, collectionName),
          where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
          where(nameField, '==', oldName),
        )
      );
      byNameSnap.docs.forEach((d) => {
        const data = d.data();
        // Skip: already caught by the by-id query (safe no-op)
        if (data.customerId === customerId) return;
        // Skip: doc has a DIFFERENT customerId — not our record to rename
        if (data.customerId) return;
        // Genuine orphan — no customerId attached. Safe to rename.
        updates.push({
          ref: doc(db, collectionName, d.id),
          data: { [nameField]: newName, updatedAt: now },
        });
      });
    } catch (err) {
      console.error(`[cascadeCustomerNameChange] ${collectionName} by-name query failed:`, err);
    }
  }

  // Collect from all four collections in parallel
  await Promise.all([
    collect('jobs', 'client', ['linkedCustomerName']),
    collect('faults', 'clientName'),
    collect('deals', 'customerName'),
    collect('customer_services', 'customerName'),
  ]);

  if (updates.length === 0) return;

  // Flush via batch writes respecting the 450-per-batch cap
  let batch = writeBatch(db);
  let count = 0;
  for (const { ref, data } of updates) {
    batch.update(ref, data);
    count++;
    if (count >= CASCADE_BATCH_LIMIT) {
      await batch.commit();
      batch = writeBatch(db);
      count = 0;
    }
  }
  if (count > 0) {
    await batch.commit();
  }

  console.log(
    `[cascadeCustomerNameChange] Renamed "${oldName}" → "${newName}" across ${updates.length} linked records`
  );
}

// ============================================================================
// DELETE
// ============================================================================

/**
 * Delete a customer
 *
 * ⚠️ CRITICAL WARNING: This should ONLY be called manually by administrators
 * for data cleanup purposes. Customer records should NEVER be automatically
 * deleted by the system, even if:
 * - Deal creation fails
 * - Deal is rolled back
 * - Deal is marked as LOST
 * - Customer has no active deals
 *
 * Customers are permanent records for historical tracking and should persist
 * even after all associated deals are closed or lost.
 *
 * This will orphan any deals associated with this customer.
 */
export async function deleteCustomer(customerId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, customerId);
  await deleteDoc(docRef);
}

// ============================================================================
// MULTI-ADDRESS MANAGEMENT
// ============================================================================

/**
 * Normalize an address string for dedup comparison.
 * Strips whitespace, lowercases, removes punctuation.
 *
 * Moved to `@/utils/address` (pure — no Firebase) so the service-dedup helpers
 * and the firebase-admin CLIs share one implementation. Re-exported here so
 * existing importers keep working; prefer importing from `@/utils/address`.
 */
export { normalizeAddress };

/**
 * The customer's known-address list: structured `addresses[]` book entries plus
 * the legacy single-address field folded in when it isn't already represented.
 * Main first, otherwise insertion order.
 *
 * Shared by every "which address?" picker (services, jobs) so they all offer
 * the same set — a customer whose only address is still on the legacy field
 * must not look address-less.
 *
 * Honours `removedAddressKeys`: the legacy field is a DERIVED entry, so
 * resurrecting it here would undo an explicit removal exactly the way
 * syncServiceAddresses used to (see the tombstone docs on Customer). Stored
 * `addresses[]` rows are already the office's own list and pass through as-is.
 */
export function buildCustomerAddressBook(customer: Customer | null | undefined): CustomerAddress[] {
  if (!customer) return [];
  const list = [...(customer.addresses || [])];

  const legacy = customer.address?.trim();
  const removed = new Set(
    (customer.removedAddressKeys || []).map(normalizeAddress).filter(Boolean),
  );
  if (
    legacy &&
    !removed.has(normalizeAddress(legacy)) &&
    !list.some((a) => normalizeAddress(a.address) === normalizeAddress(legacy))
  ) {
    list.push({
      id: `legacy-${customer.id || 'primary'}`,
      label: 'Main',
      address: legacy,
      city: customer.city,
      state: customer.state,
      postcode: customer.postcode || customer.zipCode,
      isPrimary: !(customer.addresses || []).some((a) => a.isPrimary),
      source: 'manual',
      createdAt: customer.createdAt || new Date().toISOString(),
    });
  }

  return list.sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));
}

/**
 * Check if an address already exists in the customer's addresses array.
 */
export function addressExists(
  existing: CustomerAddress[],
  newAddress: string,
): boolean {
  const norm = normalizeAddress(newAddress);
  if (!norm) return false;
  return existing.some((a) => normalizeAddress(a.address) === norm);
}

/**
 * The legacy single-address fields (`customer.address`, `.city`, …) predate the
 * addresses[] book but are still what jobs, search rows, invoices and the Xero
 * contact sync read. We keep them mirroring whichever entry is Main so the two
 * representations can't disagree — otherwise "remove the old address" leaves it
 * showing everywhere the book isn't read.
 *
 * `null` clears them (empty strings, not deleteField, so readers that do
 * `customer.address?.trim()` keep working).
 */
function legacyAddressMirror(primary: CustomerAddress | null): Partial<Customer> {
  return {
    address: primary?.address || '',
    addressLine2: primary?.addressLine2 || '',
    city: primary?.city || '',
    state: primary?.state || '',
    postcode: primary?.postcode || '',
    // Both spellings exist on the schema and different forms read different
    // ones — write them together or they drift.
    zipCode: primary?.postcode || '',
  };
}

/** Existing tombstones as a normalized Set. */
function tombstoneSet(data: Customer): Set<string> {
  return new Set(
    (data.removedAddressKeys || []).map(normalizeAddress).filter(Boolean),
  );
}

/**
 * Add a new address to the customer's addresses array.
 * If isPrimary is true, clears primary flag on existing addresses.
 *
 * Returns the address id — the new one, or the existing entry's id when the
 * address is already in the book (callers can link to it either way).
 */
export async function addCustomerAddress(
  customerId: string,
  addr: Omit<CustomerAddress, 'id' | 'createdAt'>,
): Promise<string | null> {
  if (!addr.address?.trim()) return null;

  const docRef = doc(db, COLLECTION_NAME, customerId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return null;

  const data = snap.data() as Customer;
  const existing = data.addresses || [];

  // Dedup — hand back the existing entry rather than creating a twin.
  const norm = normalizeAddress(addr.address);
  const duplicate = existing.find((a) => normalizeAddress(a.address) === norm);
  if (duplicate) return duplicate.id;

  const newAddr: CustomerAddress = {
    ...addr,
    tags: dedupeAddressTags(addr.tags),
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
  };

  // If this is set as primary, clear primary on all others
  let updatedAddresses = existing;
  if (newAddr.isPrimary) {
    updatedAddresses = existing.map((a) => ({ ...a, isPrimary: false }));
  }

  // Deliberately re-adding an address lifts its tombstone, so service sync is
  // free to keep it in step again. This is also the "undo my delete" path.
  const tombstones = tombstoneSet(data);
  tombstones.delete(norm);

  await updateDoc(docRef, stripUndefinedDeep({
    addresses: [...updatedAddresses, newAddr],
    removedAddressKeys: [...tombstones],
    ...(newAddr.isPrimary ? legacyAddressMirror(newAddr) : {}),
    updatedAt: new Date().toISOString(),
  }));

  return newAddr.id;
}

/** Fields of a saved address the office can edit after the fact. */
export type CustomerAddressPatch = Partial<
  Pick<
    CustomerAddress,
    | 'label'
    | 'address'
    | 'addressLine2'
    | 'city'
    | 'state'
    | 'postcode'
    | 'country'
    | 'tags'
    | 'isPrimary'
    | 'siteContactId'
  >
>;

/**
 * Edit a saved address in place — label, tags, or the address itself.
 *
 * Only keys present on `patch` are touched; passing a key as `undefined`
 * clears that field (stripUndefinedDeep drops it from the written object), so
 * a blanked form input does what the user expects.
 *
 * Throws when the edit would collide with another entry in the same book —
 * two identical addresses can never be told apart again by the dedup pass.
 */
export async function updateCustomerAddress(
  customerId: string,
  addressId: string,
  patch: CustomerAddressPatch,
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, customerId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return;

  const data = snap.data() as Customer;
  const existing = data.addresses || [];
  const target = existing.find((a) => a.id === addressId);
  if (!target) return;

  const nextStreet = 'address' in patch ? (patch.address || '').trim() : target.address;
  if (!nextStreet) throw new Error('An address needs a street address.');

  const others = existing.filter((a) => a.id !== addressId);
  if (addressExists(others, nextStreet)) {
    throw new Error("That address is already in this customer's address book.");
  }

  const updatedAddr: CustomerAddress = {
    ...target,
    ...patch,
    address: nextStreet,
    tags: 'tags' in patch ? dedupeAddressTags(patch.tags) : target.tags,
    updatedAt: new Date().toISOString(),
  };

  // Promoting to Main demotes everyone else; an entry that was already Main
  // stays Main unless the patch explicitly says otherwise.
  const becomesPrimary = updatedAddr.isPrimary === true;
  const addresses = existing.map((a) => {
    if (a.id === addressId) return updatedAddr;
    return becomesPrimary ? { ...a, isPrimary: false } : a;
  });

  const primary = addresses.find((a) => a.isPrimary) || null;

  // Renaming into a previously-removed address counts as bringing it back.
  const tombstones = tombstoneSet(data);
  tombstones.delete(normalizeAddress(nextStreet));

  await updateDoc(docRef, stripUndefinedDeep({
    addresses,
    removedAddressKeys: [...tombstones],
    // Only re-mirror when the Main address is the one that moved, so editing a
    // holiday house never rewrites the customer's headline address.
    ...(primary?.id === addressId ? legacyAddressMirror(primary) : {}),
    updatedAt: new Date().toISOString(),
  }));
}

/**
 * Remove an address from the customer's addresses array by ID.
 *
 * Removal is more than an array filter, because the same address can be
 * reachable from three places and syncServiceAddresses() rebuilds from all of
 * them on every customer open:
 *
 *   1. the addresses[] entry            → filtered out here
 *   2. the legacy single-address field  → cleared when it's the same address
 *   3. a linked customer_services doc   → can't be edited from here, so the
 *                                         normalized string is tombstoned
 *
 * Miss any one and the address reappears the next time the modal is opened.
 */
export async function removeCustomerAddress(
  customerId: string,
  addressId: string,
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, customerId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return;

  const data = snap.data() as Customer;
  const existing = data.addresses || [];
  const removed = existing.find((a) => a.id === addressId);
  let remaining = existing.filter((a) => a.id !== addressId);

  const removedNorm = normalizeAddress(removed?.address || '');
  const legacyNorm = normalizeAddress(data.address || '');
  const legacyMatches = !!removedNorm && removedNorm === legacyNorm;

  const tombstones = tombstoneSet(data);
  if (removedNorm) tombstones.add(removedNorm);

  // Never leave a non-empty book with no Main address — the pickers and the
  // legacy mirror both assume one exists.
  if (removed?.isPrimary && remaining.length > 0 && !remaining.some((a) => a.isPrimary)) {
    remaining = remaining.map((a, i) => (i === 0 ? { ...a, isPrimary: true } : a));
  }

  const clearsLegacy = legacyMatches || !!removed?.isPrimary;

  await updateDoc(docRef, stripUndefinedDeep({
    addresses: remaining,
    removedAddressKeys: [...tombstones],
    ...(clearsLegacy
      ? legacyAddressMirror(remaining.find((a) => a.isPrimary) || null)
      : {}),
    updatedAt: new Date().toISOString(),
  }));
}

/**
 * Set an address as the Main (primary) default.
 */
export async function setPrimaryAddress(
  customerId: string,
  addressId: string,
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, customerId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return;

  const data = snap.data() as Customer;
  const existing = data.addresses || [];
  // Guard a stale/synthetic id — without this the mirror below would wipe the
  // customer's headline address because nothing came back Main.
  if (!existing.some((a) => a.id === addressId)) return;

  const updated = existing.map((a) => ({
    ...a,
    isPrimary: a.id === addressId,
  }));

  await updateDoc(docRef, stripUndefinedDeep({
    addresses: updated,
    ...legacyAddressMirror(updated.find((a) => a.isPrimary) || null),
    updatedAt: new Date().toISOString(),
  }));
}

/**
 * Sync addresses from linked customer_services into the customer's addresses array.
 * Only adds addresses that don't already exist (dedup by normalized address string).
 * Also includes the legacy single address field if not already in the array.
 *
 * Runs on every customer open, so it is the thing that used to resurrect
 * deleted addresses. Anything in `removedAddressKeys` is skipped — see
 * removeCustomerAddress().
 */
export async function syncServiceAddresses(
  customerId: string,
): Promise<number> {
  const docRef = doc(db, COLLECTION_NAME, customerId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return 0;

  const customer = { id: snap.id, ...snap.data() } as Customer;
  const existing = customer.addresses || [];
  const toAdd: CustomerAddress[] = [];

  const removedKeys = tombstoneSet(customer);
  const wasRemoved = (addr: string) => {
    const norm = normalizeAddress(addr);
    return !!norm && removedKeys.has(norm);
  };

  // 1. Migrate the legacy single-address field if it's not already in the array
  if (customer.address && !wasRemoved(customer.address) && !addressExists(existing, customer.address)) {
    toAdd.push({
      id: crypto.randomUUID(),
      label: 'Main',
      address: customer.address,
      addressLine2: customer.addressLine2,
      city: customer.city,
      state: customer.state,
      postcode: customer.postcode || customer.zipCode,
      country: customer.country || 'Australia',
      isPrimary: true,
      source: 'manual',
      createdAt: new Date().toISOString(),
    });
  }

  // 2. Query customer_services for this customer
  const servicesQuery = query(
    collection(db, 'customer_services'),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('customerId', '==', customerId),
  );
  const servicesSnap = await getDocs(servicesQuery);

  for (const svcDoc of servicesSnap.docs) {
    const svc = svcDoc.data();
    const svcAddress = svc.address;
    if (!svcAddress?.trim()) continue;

    // The office removed this address by hand — the service still carries it,
    // but re-adding it here is exactly the bug this list exists to stop.
    if (wasRemoved(svcAddress)) continue;

    // Check against both existing and newly-added addresses
    const allAddresses = [...existing, ...toAdd];
    if (addressExists(allAddresses, svcAddress)) continue;

    toAdd.push({
      id: crypto.randomUUID(),
      label: svc.serviceType || 'Service',
      address: svcAddress,
      city: svc.city,
      state: svc.state,
      postcode: svc.zipCode,
      country: 'Australia',
      isPrimary: false,
      source: 'service',
      sourceServiceType: svc.serviceType,
      sourceServiceId: svcDoc.id,
      createdAt: new Date().toISOString(),
    });
  }

  if (toAdd.length === 0) return 0;

  await updateDoc(docRef, stripUndefinedDeep({
    addresses: [...existing, ...toAdd],
    updatedAt: new Date().toISOString(),
  }));

  return toAdd.length;
}

// ============================================================================
// COMPANY / GROUPING
// ============================================================================

/**
 * Get all companies (umbrella records) for the current tenant.
 *
 * Tenant filter via union-read so canonical + legacy alias docs both surface.
 * Backfilled in PR #978 — every customer doc now has tenantId set.
 */
export async function getCompanies(): Promise<Customer[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('isCompany', '==', true),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as Customer)
    .sort((a, b) => (a.companyName || a.name || '').localeCompare(b.companyName || b.name || ''));
}

/**
 * Get all child customers (sites) linked to a given company.
 */
export async function getChildCustomers(companyId: string): Promise<Customer[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('parentCompanyId', '==', companyId),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as Customer)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/**
 * Link a customer to a parent company.
 * Denormalizes the parent's display name so UIs can render without an extra fetch.
 * Pass `null` as companyId to unlink.
 */
export async function linkCustomerToCompany(
  customerId: string,
  companyId: string | null,
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, customerId);

  if (!companyId) {
    await updateDoc(docRef, {
      parentCompanyId: null,
      parentCompanyName: null,
      updatedAt: new Date().toISOString(),
    });
    return;
  }

  const parent = await getCustomer(companyId);
  if (!parent || !parent.isCompany) {
    throw new Error('Target customer is not a company record');
  }

  await updateDoc(docRef, {
    parentCompanyId: companyId,
    parentCompanyName: parent.companyName || parent.name || '',
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Propagate a company's renamed companyName down to its child records' parentCompanyName.
 * Call this whenever a company record's name changes.
 */
export async function syncChildrenCompanyName(companyId: string): Promise<number> {
  const parent = await getCustomer(companyId);
  if (!parent || !parent.isCompany) return 0;

  const children = await getChildCustomers(companyId);
  if (children.length === 0) return 0;

  const name = parent.companyName || parent.name || '';
  const batch = writeBatch(db);
  const now = new Date().toISOString();

  for (const child of children) {
    if (!child.id) continue;
    if (child.parentCompanyName === name) continue;
    batch.update(doc(db, COLLECTION_NAME, child.id), {
      parentCompanyName: name,
      updatedAt: now,
    });
  }

  await batch.commit();
  return children.length;
}
